import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import clinicAPI from "../../../services/clinicApi";
import { updateTenant } from "../../../store/authSlice";
import { trackActivationComplete } from "../../../analytics/metrikaGoals";
import s from "../clinic.module.css";
import DirectionStepForm from "./DirectionStepForm";
import { emptyGroupDraft } from "./GroupDraftFields";
import GroupsStepForm from "./GroupsStepForm";
import ScheduleStepForm from "./ScheduleStepForm";
import MembershipsStepForm from "./MembershipsStepForm";
import VenueStepForm from "./VenueStepForm";
import {
  addMinutesToTime,
  crossVenueScheduleConfirmText,
  emptyScheduleDraft,
  findCrossVenueScheduleConflicts,
  groupBranchMeta,
  slotsToEntries,
} from "./scheduleStepUtils";
import {
  collectDirectionNames,
  emptyDirectionSelection,
  selectionFromDirections,
} from "../../../constants/onboardingSports";
import { Button, Field, Input, useToast } from "../../ui";
import OnboardingSpotlight from "./OnboardingSpotlight";
import { ONBOARDING_TOUR_STEPS } from "./onboardingTourSteps";
import {
  dismissOnboarding,
  getOnboardingMeta,
  isOnboardingCollapsed,
  isOnboardingDismissed,
  isOnboardingTourSkipped,
  saveOnboardingMeta,
  setOnboardingCollapsed,
  skipOnboardingTour,
} from "./onboardingStorage";
import pwa from "./onboardingPwa.module.css";

function renderAppModeLayer(node) {
  if (typeof document === "undefined") return node;
  return createPortal(node, document.body);
}

const ONBOARD_RING_R = 22;
const ONBOARD_RING_C = 2 * Math.PI * ONBOARD_RING_R;

const TOTAL_STEPS = 6;

const STEPS = [
  {
    id: "profile",
    num: 1,
    icon: "🏢",
    title: "Ваша секция",
    hint: "Название и контакты",
    sheetTitle: "Ваша секция",
    sheetSub: "Как клуб называется и как с вами связаться",
  },
  {
    id: "direction",
    num: 2,
    icon: "🥋",
    title: "Направление",
    hint: "Вид спорта",
    sheetTitle: "Направление",
    sheetSub: "Выберите один или несколько видов спорта",
  },
  {
    id: "venue",
    num: 3,
    icon: "📍",
    title: "Зал",
    hint: "Место занятий",
    sheetTitle: "Зал",
    sheetSub: "Название и адрес площадки",
  },
  {
    id: "group",
    num: 4,
    icon: "👥",
    title: "Группы",
    hint: "Можно несколько",
    sheetTitle: "Группы",
    sheetSub: "Направление, зал и название группы",
  },
  {
    id: "schedule",
    num: 5,
    icon: "📅",
    title: "Расписание",
    hint: "Для каждой группы",
    sheetTitle: "Расписание",
    sheetSub: "Дни и время тренировок по группам",
  },
  {
    id: "memberships",
    num: 6,
    icon: "🎫",
    title: "Абонементы",
    hint: "Тарифы",
    sheetTitle: "Тарифы абонементов",
    sheetSub: "Хотя бы один тариф — по занятиям или по сроку",
  },
];

function trainerFirstName(fullName) {
  const parts = String(fullName || "Тренер").trim().split(/\s+/);
  return parts[0] || "Тренер";
}

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Доброе утро";
  if (h < 18) return "Добрый день";
  return "Добрый вечер";
}

function buildStepState(setup, meta, tenant) {
  const tenantName = String(tenant?.name || "").trim();
  return {
    profile:
      Boolean(meta.sectionSaved) ||
      Boolean(setup?.section_configured) ||
      tenantName.length > 0,
    direction:
      (setup?.directions_count ?? 0) > 0 || Boolean(meta.directionId),
    venue: (setup?.branches_count ?? 0) > 0 || Boolean(meta.venueSaved),
    group: (setup?.groups_count ?? 0) > 0 || Boolean(meta.groupId),
    schedule:
      (setup?.schedule_slots_count ?? 0) > 0 ||
      (setup?.training_sessions_count ?? 0) > 0 ||
      Boolean(meta.scheduleDays?.length),
    memberships:
      (setup?.membership_plans_count ?? 0) > 0 ||
      Boolean(meta.membershipsSaved),
  };
}

export function isOnboardingSetupComplete(setup, tenantId, tenant) {
  if (!tenantId) return false;
  const meta = getOnboardingMeta(tenantId);
  const stepsDone = buildStepState(setup, meta, tenant);
  return Object.values(stepsDone).filter(Boolean).length >= TOTAL_STEPS;
}

export { isOnboardingDismissed, dismissOnboarding };
export { resetOnboarding } from "./onboardingStorage";

export default function AdminOnboarding({
  tenant,
  profile,
  setup,
  onRefresh,
  onDismiss,
  appMode = false,
  children,
}) {
  const toast = useToast();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const tenantId = tenant?.id;
  const [metaVersion, setMetaVersion] = useState(0);
  const meta = useMemo(
    () => getOnboardingMeta(tenantId),
    [tenantId, metaVersion],
  );
  const bumpMeta = () => setMetaVersion((v) => v + 1);

  const stepsDone = useMemo(
    () => buildStepState(setup, meta, tenant),
    [setup, meta, tenant],
  );
  const doneCount = Object.values(stepsDone).filter(Boolean).length;
  const allDone = doneCount >= TOTAL_STEPS;
  const progressPct = Math.round((doneCount / TOTAL_STEPS) * 100);

  useEffect(() => {
    if (allDone && tenantId) {
      trackActivationComplete("onboarding_complete", tenantId);
    }
  }, [allDone, tenantId]);

  const [activeStep, setActiveStep] = useState(null);
  const [saving, setSaving] = useState(false);
  const [branches, setBranches] = useState([]);
  const [directions, setDirections] = useState([]);
  const [groups, setGroups] = useState([]);
  const [groupAdding, setGroupAdding] = useState(false);
  const [schedulesByGroupId, setSchedulesByGroupId] = useState({});
  const [scheduleTrainers, setScheduleTrainers] = useState([]);
  const [scheduleDefaultTrainerId, setScheduleDefaultTrainerId] = useState(null);
  const [activeScheduleGroupId, setActiveScheduleGroupId] = useState(null);
  const [tourSkipped, setTourSkipped] = useState(() =>
    isOnboardingTourSkipped(tenantId),
  );

  const currentStepId = useMemo(() => {
    const first = STEPS.find((st) => !stepsDone[st.id] && !isStepLocked(st.id, stepsDone));
    return first?.id || STEPS[STEPS.length - 1].id;
  }, [stepsDone]);

  const formStepId =
    activeStep || (appMode && !allDone ? currentStepId : null);

  useEffect(() => {
    if (!tenantId || meta.sectionSaved) return;
    const tenantName = String(tenant?.name || "").trim();
    if (setup?.section_configured || tenantName.length > 0) {
      saveOnboardingMeta(tenantId, { sectionSaved: true });
      bumpMeta();
    }
  }, [tenantId, tenant?.name, setup?.section_configured, meta.sectionSaved]);

  const refresh = useCallback(async () => {
    if (onRefresh) await onRefresh();
  }, [onRefresh]);

  const reloadBranches = useCallback(async () => {
    try {
      const data = await clinicAPI.getBranches();
      const list = (data.branches || []).filter((b) => b.is_active !== false);
      setBranches(list);
      return list;
    } catch {
      setBranches([]);
      return [];
    }
  }, []);

  const loadBranches = useCallback(async () => reloadBranches(), [reloadBranches]);

  const loadDirections = useCallback(async () => {
    try {
      const data = await clinicAPI.getSportDirections();
      setDirections(data.directions || []);
    } catch {
      setDirections([]);
    }
  }, []);

  const reloadGroups = useCallback(async () => {
    try {
      const data = await clinicAPI.getSportGroupsSummary();
      const list = (data.groups || []).filter((g) => g.is_active !== false);
      setGroups(list);
      return list;
    } catch {
      try {
        const data = await clinicAPI.getSpecializations();
        const list = (data.specializations || []).filter(
          (g) => g.is_active !== false,
        );
        setGroups(list);
        return list;
      } catch {
        setGroups([]);
        return [];
      }
    }
  }, []);

  useEffect(() => {
    if (
      formStepId === "venue" ||
      formStepId === "group" ||
      formStepId === "schedule" ||
      formStepId === "memberships"
    ) {
      loadBranches();
    }
    if (
      formStepId === "group" ||
      formStepId === "direction" ||
      formStepId === "schedule"
    ) {
      loadDirections();
    }
    if (
      formStepId === "group" ||
      formStepId === "schedule" ||
      formStepId === "memberships"
    ) {
      reloadGroups();
    }
  }, [formStepId, loadBranches, loadDirections, reloadGroups]);

  const [profileForm, setProfileForm] = useState({
    name: tenant?.name || "Моя секция",
    city: meta.city || tenant?.address || "",
    phone: tenant?.phone || "",
  });
  const [directionForm, setDirectionForm] = useState(emptyDirectionSelection);
  const [venueDraft, setVenueDraft] = useState("");
  const [venueAddressDraft, setVenueAddressDraft] = useState("");
  const [venueAdding, setVenueAdding] = useState(false);
  const [groupForm, setGroupForm] = useState(() =>
    emptyGroupDraft([], []),
  );
  const [scheduleGroupLabel, setScheduleGroupLabel] = useState({
    name: meta.groupName || "",
    direction: meta.directionName || "",
    branch: meta.venueName || "",
  });
  const [membershipPlans, setMembershipPlans] = useState([]);
  const [membershipPlanForm, setMembershipPlanForm] = useState({
    name: "",
    plan_type: "visits",
    visits_count: 8,
    duration_days: 30,
    price: "",
  });
  const [planAdding, setPlanAdding] = useState(false);

  useEffect(() => {
    if (!tenant?.id) return;
    setProfileForm((prev) => ({
      name: tenant.name || prev.name || "Моя секция",
      city: meta.city || tenant.address || prev.city || "",
      phone: tenant.phone != null && tenant.phone !== "" ? tenant.phone : prev.phone,
    }));
  }, [tenant?.id, tenant?.name, tenant?.address, tenant?.phone, meta.city]);

  const finishOnboardingToHome = useCallback(() => {
    saveOnboardingMeta(tenantId, { awaitingGuide: true });
    dismissOnboarding(tenantId);
    onDismiss?.();
    navigate("/manage/app", { replace: true });
  }, [tenantId, onDismiss, navigate]);

  const closeSheet = () => setActiveStep(null);

  useEffect(() => {
    if (!appMode || allDone) return;
    setActiveStep((prev) => {
      if (prev) return prev;
      return currentStepId;
    });
  }, [appMode, allDone, currentStepId]);

  const showTour =
    !appMode && !allDone && !tourSkipped && !isOnboardingCollapsed(tenantId);
  const tourStepMeta = ONBOARDING_TOUR_STEPS[currentStepId] || null;
  const spotlightTarget = `[data-onboarding-step="${currentStepId}"]`;
  const spotlightCopy = tourStepMeta;

  const handleSkipTour = () => {
    skipOnboardingTour(tenantId);
    setTourSkipped(true);
  };

  const openStep = (stepId) => {
    if (isStepLocked(stepId, stepsDone)) return;
    setActiveStep(stepId);
  };

  useEffect(() => {
    if (!formStepId) return undefined;
    let cancelled = false;

    (async () => {
      if (formStepId === "profile") {
        try {
          const data = await clinicAPI.getTenantSettings();
          if (cancelled) return;
          const t = data.tenant;
          setProfileForm({
            name: t?.name || "Моя секция",
            city: meta.city || t?.address || "",
            phone: t?.phone || "",
          });
        } catch {
          setProfileForm({
            name: tenant?.name || "Моя секция",
            city: meta.city || tenant?.address || "",
            phone: tenant?.phone || "",
          });
        }
        return;
      }

      if (formStepId === "direction") {
        try {
          const data = await clinicAPI.getSportDirections();
          if (cancelled) return;
          const list = data.directions || [];
          setDirections(list);
          setDirectionForm(selectionFromDirections(list));
        } catch {
          /* keep current form */
        }
        return;
      }

      if (formStepId === "venue") {
        try {
          const data = await clinicAPI.getBranches();
          if (cancelled) return;
          const list = (data.branches || []).filter((b) => b.is_active !== false);
          setBranches(list);
          setVenueDraft("");
          setVenueAddressDraft("");
        } catch {
          /* keep current form */
        }
        return;
      }

      if (formStepId === "group") {
        const dirData = await clinicAPI.getSportDirections().catch(() => ({
          directions: [],
        }));
        const branchData = await clinicAPI.getBranches().catch(() => ({
          branches: [],
        }));
        if (cancelled) return;
        const dirList = dirData.directions || [];
        const branchList = (branchData.branches || []).filter(
          (b) => b.is_active !== false,
        );
        setDirections(dirList);
        setBranches(branchList);
        await reloadGroups();
        if (cancelled) return;
        setGroupForm(
          emptyGroupDraft(dirList, branchList, {
            directionId: meta.directionId,
            branchId: meta.branchId,
          }),
        );
        return;
      }

      if (
        formStepId === "schedule" ||
        formStepId === "memberships"
      ) {
        const list = await reloadGroups();
        if (cancelled) return;

        if (formStepId === "memberships") {
          try {
            const planData = await clinicAPI.getMembershipPlans();
            if (cancelled) return;
            setMembershipPlans(planData.plans || []);
          } catch {
            setMembershipPlans([]);
          }
          return;
        }

        const branchRes = await clinicAPI.getBranches().catch(() => ({
          branches: [],
        }));
        const branchList = (branchRes.branches || []).filter(
          (b) => b.is_active !== false,
        );
        if (!cancelled) setBranches(branchList);

        const doctorsRes = await clinicAPI
          .getDoctors({ active: true })
          .catch(() => ({ doctors: [], default_trainer_id: null }));
        if (!cancelled) {
          setScheduleTrainers(doctorsRes.doctors || []);
          setScheduleDefaultTrainerId(doctorsRes.default_trainer_id || null);
        }
        const fallbackTrainerId = doctorsRes.default_trainer_id || null;

        const byId = {};
        for (const g of list) {
          try {
            const data = await clinicAPI.getGroupScheduleSlots(g.id);
            const slotDefault =
              data.default_trainer_id || fallbackTrainerId || null;
            const entries = slotsToEntries(data.slots || []).map((entry) => ({
              ...entry,
              trainer_id: entry.trainer_id || slotDefault || null,
            }));
            byId[g.id] = {
              ...emptyScheduleDraft(),
              entries,
              start_time: entries[0]?.start_time || "16:00",
              duration_min: entries[0]?.duration_min || 60,
              draft_trainer_id: slotDefault,
            };
          } catch {
            byId[g.id] = emptyScheduleDraft();
          }
        }
        if (cancelled) return;
        setSchedulesByGroupId(byId);
        const firstId =
          list.find((g) => g.id === activeScheduleGroupId)?.id ||
          list.find((g) => g.id === meta.groupId)?.id ||
          list[0]?.id ||
          null;
        setActiveScheduleGroupId(firstId);
        const active = list.find((g) => g.id === firstId);
        const { name: branch } = groupBranchMeta(active || {}, branchList);
        setScheduleGroupLabel({
          name: active?.name || "",
          direction:
            active?.direction?.name || active?.sport_direction || "",
          branch,
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [formStepId, tenant, meta, reloadGroups]);

  const finishStepSave = (stepId) => {
    const idx = STEPS.findIndex((st) => st.id === stepId);
    if (idx < 0) return;
    if (idx >= STEPS.length - 1) {
      // Последний шаг → сводка с плашкой руководства
      finishOnboardingToHome();
      return;
    }
    setActiveStep(STEPS[idx + 1].id);
  };

  const activeScheduleForm = useMemo(
    () => schedulesByGroupId[activeScheduleGroupId] || emptyScheduleDraft(),
    [schedulesByGroupId, activeScheduleGroupId],
  );

  const groupsForSchedule = useMemo(
    () =>
      groups.map((g) => {
        const { name: branchName } = groupBranchMeta(g, branches);
        return {
          ...g,
          branchName,
          scheduleCount: (schedulesByGroupId[g.id]?.entries || []).length,
        };
      }),
    [groups, schedulesByGroupId, branches],
  );

  const scheduleCrossVenueConflicts = useMemo(
    () =>
      findCrossVenueScheduleConflicts(groups, schedulesByGroupId, branches),
    [groups, schedulesByGroupId, branches],
  );

  const pendingScheduleNames = useMemo(
    () =>
      groups
        .filter((g) => !(schedulesByGroupId[g.id]?.entries || []).length)
        .map((g) => g.name),
    [groups, schedulesByGroupId],
  );

  const scheduleSubmitEnabled = useMemo(
    () =>
      groups.length > 0 &&
      groups.every(
        (g) => (schedulesByGroupId[g.id]?.entries || []).length > 0,
      ),
    [groups, schedulesByGroupId],
  );

  const updateActiveScheduleForm = useCallback(
    (updater) => {
      setSchedulesByGroupId((prev) => {
        const id = activeScheduleGroupId;
        if (!id) return prev;
        const cur = prev[id] || emptyScheduleDraft();
        const next =
          typeof updater === "function" ? updater(cur) : updater;
        return { ...prev, [id]: next };
      });
    },
    [activeScheduleGroupId],
  );

  const selectScheduleGroup = useCallback(
    (groupId) => {
      setActiveScheduleGroupId(groupId);
      const g = groups.find((gr) => gr.id === groupId);
      if (g) {
        const { name: branch } = groupBranchMeta(g, branches);
        setScheduleGroupLabel({
          name: g.name,
          direction: g.direction?.name || g.sport_direction || "",
          branch,
        });
      }
      setSchedulesByGroupId((prev) => {
        if (prev[groupId]) return prev;
        return { ...prev, [groupId]: emptyScheduleDraft() };
      });
    },
    [groups, branches],
  );

  if (!tenantId) {
    return children || null;
  }

  if (isOnboardingDismissed(tenantId)) {
    return children || null;
  }
  if (isOnboardingSetupComplete(setup, tenantId, tenant) && !allDone) {
    return children || null;
  }

  const handleDismiss = () => {
    saveOnboardingMeta(tenantId, { awaitingGuide: false });
    dismissOnboarding(tenantId);
    onDismiss?.();
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await clinicAPI.updateTenantSettings({
        name: profileForm.name.trim() || "Моя секция",
        phone: profileForm.phone.trim(),
        address: profileForm.city.trim(),
      });
      const updated = res.tenant;
      if (updated) {
        dispatch(updateTenant(updated));
        setProfileForm({
          name: updated.name || profileForm.name,
          city: updated.address || profileForm.city,
          phone: updated.phone || "",
        });
      }
      saveOnboardingMeta(tenantId, {
        sectionSaved: true,
        city: updated?.address || profileForm.city,
      });
      bumpMeta();
      toast.success("Сохранено");
      await refresh();
      finishStepSave("profile");
    } catch (err) {
      toast.error(
        err.response?.data?.error ||
          err.response?.data?.message ||
          "Не удалось сохранить",
      );
    } finally {
      setSaving(false);
    }
  };

  const saveDirection = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const names = collectDirectionNames(directionForm);
      if (!names.length) throw new Error("Выберите хотя бы одно направление");

      let list = directions;
      if (!list.length) {
        const data = await clinicAPI.getSportDirections();
        list = data.directions || [];
      }

      const wanted = new Set(names.map((n) => n.toLowerCase()));
      const byName = new Map(
        list.map((d) => [String(d.name).trim().toLowerCase(), d]),
      );

      for (const d of list) {
        if (!wanted.has(String(d.name).trim().toLowerCase())) {
          await clinicAPI.deleteSportDirection(d.id);
        }
      }

      let firstDirectionId = meta.directionId || null;
      for (const name of names) {
        const key = name.toLowerCase();
        const existing = byName.get(key);
        if (existing) {
          if (!firstDirectionId) firstDirectionId = existing.id;
          continue;
        }
        const res = await clinicAPI.createSportDirection({
          name,
          onboarding: true,
        });
        const created = res.direction;
        if (created) {
          byName.set(key, created);
          if (!firstDirectionId) firstDirectionId = created.id;
        }
      }

      saveOnboardingMeta(tenantId, {
        directionId: firstDirectionId,
        directionName: names[0],
        directionNames: names,
      });
      bumpMeta();
      const dirsRes = await clinicAPI.getSportDirections();
      const fresh = dirsRes.directions || [];
      setDirections(fresh);
      setDirectionForm(selectionFromDirections(fresh));
      toast.success(
        names.length === 1
          ? "Сохранено"
          : `Сохранено направлений: ${names.length}`,
      );
      await refresh();
      finishStepSave("direction");
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Ошибка");
    } finally {
      setSaving(false);
    }
  };

  const addVenue = async () => {
    const name = venueDraft.trim();
    const address = venueAddressDraft.trim();
    if (!name) return;
    if (!address) {
      toast.error("Укажите адрес зала");
      return;
    }
    const dup = branches.some(
      (b) => b.name.toLowerCase() === name.toLowerCase(),
    );
    if (dup) {
      toast.info("Такой зал уже в списке");
      setVenueDraft("");
      setVenueAddressDraft("");
      return;
    }
    setVenueAdding(true);
    try {
      await clinicAPI.createBranch({ name, address, is_active: true });
      const list = await reloadBranches();
      setVenueDraft("");
      setVenueAddressDraft("");
      if (list.length === 1) {
        saveOnboardingMeta(tenantId, {
          venueName: list[0].name,
          branchId: list[0].id,
        });
        setGroupForm((f) => ({ ...f, branch_id: list[0].id }));
        bumpMeta();
      }
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || "Ошибка");
    } finally {
      setVenueAdding(false);
    }
  };

  const removeVenue = async (branchId) => {
    try {
      await clinicAPI.deleteBranch(branchId);
      const list = await reloadBranches();
      if (meta.branchId === branchId) {
        const next = list[0];
        saveOnboardingMeta(tenantId, {
          branchId: next?.id || null,
          venueName: next?.name || "",
        });
        setGroupForm((f) => ({ ...f, branch_id: next?.id || "" }));
        bumpMeta();
      }
    } catch (err) {
      toast.error(err.response?.data?.error || "Не удалось удалить");
    }
  };

  const continueVenue = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (!branches.length) {
        throw new Error("Добавьте хотя бы один зал");
      }
      const primary = branches[0];
      saveOnboardingMeta(tenantId, {
        venueName: primary.name,
        branchId: primary.id,
        venueSaved: true,
      });
      bumpMeta();
      setGroupForm((f) => ({ ...f, branch_id: primary.id }));
      await refresh();
      finishStepSave("venue");
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || "Ошибка");
    } finally {
      setSaving(false);
    }
  };

  const addGroup = async () => {
    const name = groupForm.name.trim();
    if (!name) return;
    const dup = groups.some(
      (g) => g.name.toLowerCase() === name.toLowerCase(),
    );
    if (dup) {
      toast.info("Группа с таким названием уже есть");
      setGroupForm((f) => ({ ...f, name: "" }));
      return;
    }
    const directionId =
      groupForm.direction_id || meta.directionId || directions[0]?.id;
    const branchId =
      groupForm.branch_id || meta.branchId || branches[0]?.id;
    if (!directionId || !branchId) {
      toast.error("Нужны направление и зал");
      return;
    }
    setGroupAdding(true);
    try {
      await clinicAPI.createSpecialization({
        name,
        direction_id: directionId,
        branch_id: branchId,
        max_capacity: groupForm.max_capacity
          ? Number(groupForm.max_capacity)
          : null,
        onboarding: true,
      });
      trackActivationComplete("group_created", tenantId);
      const list = await reloadGroups();
      setGroupForm(emptyGroupDraft(directions, branches));
      if (list.length === 1) {
        const g = list[0];
        saveOnboardingMeta(tenantId, {
          groupId: g.id,
          groupName: g.name,
          groupIds: [g.id],
          directionId: g.direction_id,
          branchId: g.branch_id,
        });
        bumpMeta();
      }
      toast.success("Группа добавлена");
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || "Ошибка");
    } finally {
      setGroupAdding(false);
    }
  };

  const removeGroup = async (groupId) => {
    try {
      await clinicAPI.deactivateSpecialization(groupId);
      const list = await reloadGroups();
      setSchedulesByGroupId((prev) => {
        const next = { ...prev };
        delete next[groupId];
        return next;
      });
      if (activeScheduleGroupId === groupId) {
        setActiveScheduleGroupId(list[0]?.id || null);
      }
      if (meta.groupId === groupId) {
        const next = list[0];
        saveOnboardingMeta(tenantId, {
          groupId: next?.id || null,
          groupName: next?.name || "",
          groupIds: list.map((g) => g.id),
        });
        bumpMeta();
      }
    } catch (err) {
      toast.error(err.response?.data?.error || "Не удалось удалить");
    }
  };

  const continueGroups = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (!groups.length) throw new Error("Добавьте хотя бы одну группу");
      const primary = groups[0];
      saveOnboardingMeta(tenantId, {
        groupIds: groups.map((g) => g.id),
        groupId: primary.id,
        groupName: primary.name,
        directionId: primary.direction_id,
        directionName:
          primary.direction?.name || primary.sport_direction || "",
        branchId: primary.branch_id,
      });
      bumpMeta();
      setSchedulesByGroupId((prev) => {
        const next = { ...prev };
        for (const g of groups) {
          if (!next[g.id]) next[g.id] = emptyScheduleDraft();
        }
        return next;
      });
      setActiveScheduleGroupId(primary.id);
      await refresh();
      finishStepSave("group");
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || "Ошибка");
    } finally {
      setSaving(false);
    }
  };

  const saveAllSchedules = async (e) => {
    e.preventDefault();
    const conflicts = findCrossVenueScheduleConflicts(
      groups,
      schedulesByGroupId,
      branches,
    );
    if (conflicts.length) {
      const ok = window.confirm(crossVenueScheduleConfirmText(conflicts));
      if (!ok) return;
    }
    setSaving(true);
    try {
      if (!groups.length) throw new Error("Сначала создайте группы");
      let totalSessions = 0;
      for (const g of groups) {
        const entries = schedulesByGroupId[g.id]?.entries || [];
        if (!entries.length) {
          throw new Error(`Добавьте расписание для «${g.name}»`);
        }
        const res = await clinicAPI.saveGroupScheduleSlots(g.id, {
          slots: entries.map((entry) => ({
            weekday: entry.weekday,
            start_time: entry.start_time,
            end_time: addMinutesToTime(entry.start_time, entry.duration_min),
            branch_id: g.branch_id || g.branch?.id || null,
          })),
          generate_weeks: 4,
          regenerate_future: true,
        });
        totalSessions += res.sessions_created || 0;
      }
      saveOnboardingMeta(tenantId, {
        scheduleDays: [
          ...new Set(
            groups.flatMap(
              (g) =>
                (schedulesByGroupId[g.id]?.entries || []).map((e) => e.weekday),
            ),
          ),
        ],
      });
      bumpMeta();
      toast.success(
        totalSessions
          ? `Расписание сохранено · ${totalSessions} занятий`
          : "Расписание сохранено",
      );
      await refresh();
      finishStepSave("schedule");
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || "Ошибка");
    } finally {
      setSaving(false);
    }
  };

  const addMembershipPlan = async () => {
    const name = membershipPlanForm.name.trim();
    if (!name) {
      toast.error("Укажите название тарифа");
      return;
    }
    const durationDays = Number(membershipPlanForm.duration_days);
    if (!durationDays || durationDays < 1) {
      toast.error("Укажите срок действия в днях");
      return;
    }
    if (
      membershipPlanForm.plan_type === "visits" &&
      (!Number(membershipPlanForm.visits_count) ||
        Number(membershipPlanForm.visits_count) < 1)
    ) {
      toast.error("Укажите количество занятий");
      return;
    }
    setPlanAdding(true);
    try {
      await clinicAPI.createMembershipPlan({
        name,
        plan_type: membershipPlanForm.plan_type,
        price: Number(membershipPlanForm.price) || 0,
        visits_count:
          membershipPlanForm.plan_type === "visits"
            ? Number(membershipPlanForm.visits_count)
            : null,
        duration_days: durationDays,
        freeze_days_allowed: 7,
      });
      const planData = await clinicAPI.getMembershipPlans();
      setMembershipPlans(planData.plans || []);
      setMembershipPlanForm((f) => ({ ...f, name: "", price: "" }));
      toast.success("Тариф добавлен");
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.error || "Ошибка");
    } finally {
      setPlanAdding(false);
    }
  };

  const continueMemberships = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (!membershipPlans.length) {
        throw new Error("Добавьте хотя бы один тариф");
      }
      saveOnboardingMeta(tenantId, { membershipsSaved: true });
      // Сначала закрываем мастер и ставим плашку — до refresh,
      // иначе сводка спрячет онбординг без awaitingGuide.
      finishOnboardingToHome();
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || "Ошибка");
    } finally {
      setSaving(false);
    }
  };

  const activeMeta = STEPS.find((st) => st.id === activeStep);

  const renderSheetForm = () => {
    if (!formStepId) return null;

    if (formStepId === "profile") {
      return (
        <form onSubmit={saveProfile} className={s.onboardingForm}>
          <Field label="Название секции">
            <Input
              value={profileForm.name}
              onChange={(e) =>
                setProfileForm({ ...profileForm, name: e.target.value })
              }
              placeholder="Моя секция"
              autoFocus
            />
          </Field>
          <Field label="Город">
            <Input
              value={profileForm.city}
              onChange={(e) =>
                setProfileForm({ ...profileForm, city: e.target.value })
              }
            />
          </Field>
          <Field label="Телефон">
            <Input
              value={profileForm.phone}
              onChange={(e) =>
                setProfileForm({ ...profileForm, phone: e.target.value })
              }
            />
          </Field>
          <SheetActions
            saving={saving}
            submitLabel={stepsDone.profile ? "Сохранить" : "Сохранить и продолжить"}
          />
        </form>
      );
    }

    if (formStepId === "direction") {
      return (
        <DirectionStepForm
          selection={directionForm}
          onChange={setDirectionForm}
          onSubmit={saveDirection}
          saving={saving}
          submitLabel={
            stepsDone.direction ? "Сохранить" : "Сохранить и продолжить"
          }
        />
      );
    }

    if (formStepId === "venue") {
      return (
        <VenueStepForm
          branches={branches}
          draft={venueDraft}
          addressDraft={venueAddressDraft}
          onDraftChange={setVenueDraft}
          onAddressDraftChange={setVenueAddressDraft}
          onAdd={addVenue}
          onRemove={removeVenue}
          onContinue={continueVenue}
          saving={saving}
          adding={venueAdding}
          submitLabel={
            stepsDone.venue ? "Сохранить" : "Сохранить и продолжить"
          }
        />
      );
    }

    if (formStepId === "group") {
      const dirOptions =
        directions.length > 0
          ? directions
          : meta.directionId
            ? [{ id: meta.directionId, name: meta.directionName }]
            : [];
      const branchOptions =
        branches.length > 0
          ? branches
          : meta.branchId
            ? [{ id: meta.branchId, name: meta.venueName || "Зал" }]
            : [];
      return (
        <GroupsStepForm
          groups={groups}
          draft={groupForm}
          onDraftChange={setGroupForm}
          directions={dirOptions}
          branches={branchOptions}
          onAdd={addGroup}
          onRemove={removeGroup}
          onContinue={continueGroups}
          saving={saving}
          adding={groupAdding}
          submitLabel={
            stepsDone.group ? "Сохранить" : "Сохранить и продолжить"
          }
        />
      );
    }

    if (formStepId === "schedule") {
      if (!groups.length) {
        return (
          <div className={s.onboardingForm}>
            <p className={s.onboardingSheetHint}>
              Сначала добавьте хотя бы одну группу на предыдущем шаге.
            </p>
          </div>
        );
      }
      return (
        <ScheduleStepForm
          form={activeScheduleForm}
          onChange={updateActiveScheduleForm}
          onSubmit={saveAllSchedules}
          saving={saving}
          submitLabel={stepsDone.schedule ? "Сохранить" : "Продолжить"}
          groupName={scheduleGroupLabel.name}
          directionName={scheduleGroupLabel.direction}
          branchName={scheduleGroupLabel.branch}
          groups={groupsForSchedule}
          activeGroupId={activeScheduleGroupId}
          onSelectGroup={selectScheduleGroup}
          submitEnabled={scheduleSubmitEnabled}
          pendingGroupNames={pendingScheduleNames}
          crossVenueConflicts={scheduleCrossVenueConflicts}
          trainers={scheduleTrainers}
          defaultTrainerId={scheduleDefaultTrainerId}
        />
      );
    }

    if (formStepId === "memberships") {
      return (
        <MembershipsStepForm
          plans={membershipPlans}
          planForm={membershipPlanForm}
          onPlanFormChange={setMembershipPlanForm}
          onAddPlan={addMembershipPlan}
          adding={planAdding}
          onContinue={continueMemberships}
          saving={saving}
          submitLabel={
            stepsDone.memberships ? "Сохранить" : "Готово"
          }
        />
      );
    }

    return null;
  };

  const stepMeta = activeMeta || STEPS.find((st) => st.id === currentStepId);

  if (appMode && stepMeta) {
    const activeId = activeStep || currentStepId;
    const ringOffset =
      ONBOARD_RING_C - (progressPct / 100) * ONBOARD_RING_C;

    return renderAppModeLayer(
      <div className={pwa.root}>
        <header className={pwa.header}>
          <div className={pwa.headerGlass}>
            <div className={pwa.headerRow}>
              <p className={pwa.greeting}>
                {greeting()}, {trainerFirstName(profile?.full_name)}
              </p>
              <button type="button" className={pwa.skipBtn} onClick={handleDismiss}>
                Позже
              </button>
            </div>
            <div className={pwa.progressRow}>
              <div
                className={pwa.progressRing}
                role="progressbar"
                aria-valuenow={progressPct}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={`Настройка клуба: ${progressPct}%`}
              >
                <svg viewBox="0 0 52 52" aria-hidden>
                  <circle
                    className={pwa.progressRingTrack}
                    cx="26"
                    cy="26"
                    r={ONBOARD_RING_R}
                  />
                  <circle
                    className={pwa.progressRingFill}
                    cx="26"
                    cy="26"
                    r={ONBOARD_RING_R}
                    strokeDasharray={ONBOARD_RING_C}
                    strokeDashoffset={ringOffset}
                  />
                </svg>
                <span className={pwa.progressRingLabel}>{progressPct}%</span>
              </div>
              <div className={pwa.progressCopy}>
                <p className={pwa.progressTitle}>Настройка клуба</p>
                <p className={pwa.progressMeta}>
                  {doneCount} из {TOTAL_STEPS} · {stepMeta.title}
                </p>
              </div>
            </div>
            <div className={pwa.stepRail} role="tablist" aria-label="Шаги настройки">
              {STEPS.map((st, idx) => {
                const done = stepsDone[st.id];
                const current = st.id === activeId;
                const locked = isStepLocked(st.id, stepsDone);
                return (
                  <Fragment key={st.id}>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={current}
                      aria-label={`${st.title}${done ? ", выполнено" : ""}`}
                      disabled={locked}
                      className={[
                        pwa.stepDot,
                        done ? pwa.stepDotDone : "",
                        current && !done ? pwa.stepDotCurrent : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      onClick={() => openStep(st.id)}
                    >
                      {done ? "✓" : st.num}
                    </button>
                    {idx < STEPS.length - 1 ? (
                      <span
                        className={[
                          pwa.stepConnector,
                          done ? pwa.stepConnectorDone : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        aria-hidden
                      />
                    ) : null}
                  </Fragment>
                );
              })}
            </div>
          </div>
        </header>

        <main className={pwa.scroll} key={activeId}>
          <div className={pwa.stepPanel}>
            <div className={pwa.heroIcon} aria-hidden>
              {stepMeta.icon}
            </div>
            <h1 className={pwa.title}>{stepMeta.sheetTitle}</h1>
            <div className={`${pwa.formCard} ${pwa.form}`}>
              {renderSheetForm()}
            </div>
          </div>
        </main>
      </div>,
    );
  }

  return (
    <div className={s.onboardingWrap}>
      <section className={s.onboardingHero}>
        <div className={s.onboardingHeroInner}>
          <div className={s.onboardingHeroText}>
            <span className={s.onboardingHeroBadge}>Первичная настройка</span>
            <h2 className={s.onboardingHeroTitle}>
              {greeting()}, {trainerFirstName(profile?.full_name)}!
            </h2>
            <p className={s.onboardingHeroSub}>
              Пройдите шаги ниже — от названия секции до тарифов абонементов.
            </p>
          </div>
          <div className={s.onboardingHeroProgress}>
            <div
              className={s.onboardingHeroRing}
              style={{ "--pct": progressPct }}
              role="progressbar"
              aria-valuenow={progressPct}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <span>{progressPct}%</span>
            </div>
            <span className={s.onboardingHeroProgressLabel}>
              {doneCount} из {TOTAL_STEPS} шагов
            </span>
          </div>
        </div>

        <ol className={s.onboardingTimeline} aria-label="Прогресс настройки">
          {STEPS.map((st) => {
            const done = stepsDone[st.id];
            const current = st.id === currentStepId && !done;
            const locked = isStepLocked(st.id, stepsDone);
            return (
              <li
                key={st.id}
                className={[
                  s.onboardingTimelineItem,
                  done ? s.onboardingTimelineDone : "",
                  current ? s.onboardingTimelineCurrent : "",
                  locked ? s.onboardingTimelineLocked : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                <span className={s.onboardingTimelineDot}>
                  {done ? "✓" : st.num}
                </span>
                <span className={s.onboardingTimelineLabel}>{st.title}</span>
              </li>
            );
          })}
        </ol>
      </section>

      <ul className={s.onboardingCardGrid}>
        {STEPS.map((step) => {
          const done = stepsDone[step.id];
          const isCurrent = step.id === currentStepId && !done;
          const locked = isStepLocked(step.id, stepsDone);
          const isOpen = activeStep === step.id;

          return (
            <li key={step.id} className={s.onboardingCardCell}>
              <button
                type="button"
                data-onboarding-step={step.id}
                className={[
                  s.onboardingCardV3,
                  done ? s.onboardingCardV3Done : "",
                  isCurrent ? s.onboardingCardV3Current : "",
                  locked ? s.onboardingCardV3Locked : "",
                  isOpen ? s.onboardingCardV3Open : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={() => openStep(step.id)}
                disabled={locked}
                title={
                  done
                    ? "Изменить данные шага"
                    : locked
                      ? "Сначала завершите предыдущие шаги"
                      : undefined
                }
              >
                <div className={s.onboardingCardV3Head}>
                  <span className={s.onboardingCardV3Num}>
                    {done ? "✓" : step.num}
                  </span>
                  <span className={s.onboardingCardV3Status}>
                    {done
                      ? "Изменить"
                      : locked
                        ? "Позже"
                        : isCurrent
                          ? "Сейчас"
                          : "Открыть"}
                  </span>
                </div>
                <span className={s.onboardingCardV3Icon} aria-hidden>
                  {step.icon}
                </span>
                <strong className={s.onboardingCardV3Title}>{step.title}</strong>
                <span className={s.onboardingCardV3Hint}>{step.hint}</span>
              </button>
            </li>
          );
        })}
      </ul>

      <p className={s.onboardingSkipRow}>
        <button type="button" className={s.onboardingSkipLink} onClick={handleDismiss}>
          Настрою позже
        </button>
      </p>

      {activeStep && activeMeta ? (
        <div className={s.onboardingSheetBackdrop} onClick={closeSheet} role="presentation">
          <aside
            className={s.onboardingSheet}
            data-onboarding-sheet=""
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="onboarding-sheet-title"
          >
            <header className={s.onboardingSheetHead}>
              <button
                type="button"
                className={s.onboardingSheetClose}
                onClick={closeSheet}
                aria-label="Закрыть"
              >
                ×
              </button>
              <div className={s.onboardingSheetProgressWrap}>
                <div
                  className={s.onboardingSheetProgressTrack}
                  role="progressbar"
                  aria-valuenow={activeMeta.num}
                  aria-valuemin={1}
                  aria-valuemax={TOTAL_STEPS}
                  aria-label={`Шаг ${activeMeta.num} из ${TOTAL_STEPS}`}
                >
                  <div
                    className={s.onboardingSheetProgressFill}
                    style={{
                      width: `${(activeMeta.num / TOTAL_STEPS) * 100}%`,
                    }}
                  />
                </div>
                <span className={s.onboardingSheetStep}>
                  Шаг {activeMeta.num} из {TOTAL_STEPS}
                </span>
              </div>
              <h3 id="onboarding-sheet-title">{activeMeta.sheetTitle}</h3>
              <p>{activeMeta.sheetSub}</p>
            </header>
            <div className={s.onboardingSheetBody}>{renderSheetForm()}</div>
          </aside>
        </div>
      ) : null}

      {allDone ? children : (
        <div className={s.onboardingDashboardPreview}>{children}</div>
      )}

      {showTour && spotlightCopy && !activeStep ? (
        <OnboardingSpotlight
          targetSelector={spotlightTarget}
          title={spotlightCopy.title}
          description={spotlightCopy.description}
          stepLabel={`Шаг ${STEPS.find((st) => st.id === currentStepId)?.num || ""} из ${TOTAL_STEPS}`}
          placement="bottom"
          onTargetActivate={() => {
            openStep(currentStepId);
          }}
          onSkip={handleSkipTour}
        />
      ) : null}
    </div>
  );
}

function isStepLocked(stepId, stepsDone) {
  const order = STEPS.map((s) => s.id);
  const idx = order.indexOf(stepId);
  if (idx <= 0) return false;
  for (let i = 0; i < idx; i++) {
    if (!stepsDone[order[i]]) return true;
  }
  return false;
}

function SheetActions({ saving, submitLabel = "Сохранить и продолжить" }) {
  return (
    <div className={s.onboardingSheetFoot}>
      <Button
        type="submit"
        disabled={saving}
        className={s.onboardingSubmitBtn}
        data-onboarding-submit=""
      >
        {saving ? "Сохранение…" : submitLabel}
      </Button>
    </div>
  );
}
